/* eslint-disable react/no-unstable-nested-components */
import React, { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import ReactLoading from 'react-loading';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import useYupValidationResolver from 'src/hooks/useYupValidationResolver';
import API from 'src/network';
import { confirmWrapper } from 'src/components/Dialog';
import { BsFillPatchCheckFill } from 'react-icons/bs';
import { DialogContentProps } from 'src/components/Dialog/Dialog';

function Input({
  label, field, error, isMandatory = false, register, ...rest
}: any) {
  return (
    <div className={twMerge('flex flex-col pt-4 w-full')}>
      <span className="pt-2 text-sm">
        {label}
        {isMandatory && <span className="text-[#B60303]">*</span>}
      </span>
      <input
        {...register(field)}
        {...rest}
        className={twMerge(
          'mt-2 px-2 py-2 border focus-visible:border-blue-600 rounded-md',
          error ? 'bg-[#b6030335] !border-red-600' : '',
        )}
      />
      {error && <span className="pl-1 text-[#B60303] text-[10px]">{error.message}</span>}
    </div>
  );
}

export type Fields = 'name' | 'email';

interface FieldsValues {
  name: string;
  email: string;
}
interface ShareJobProps extends DialogContentProps {
  jobTitle: string;
}

function ShareJob(props: ShareJobProps) {
  const { jobTitle, proceed } = props;
  const defaultState = {
    submit: false,
    success: false,
    error: false,
  };
  const [state, setState] = useState({
    ...defaultState,
  });

  function excludeTempEmails(value: string) {
    const restrictedDomains: string[] = [
      'yopmail.com',
    ];
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    const domain = value?.split('@')[1];

    if (!value || !domain) {
      return true;
    }

    if (restrictedDomains.includes(domain)) {
      return false;
    }

    if (!emailRegex.test(value)) {
      return false;
    }

    return true;
  }

  const schema = yup.object().shape({
    name: yup.string().required('Please enter the name.')
      .matches(/^[A-Za-z]+(?: [A-Za-z]+)*$/, 'Enter a valid name.'),
    email: yup.string().email('Enter valid email id.').required('Please enter the email id.')
      .test('Email', 'Temporary emails are not allowed.', excludeTempEmails),
  });

  const { register, handleSubmit, formState: { errors } } = useForm<FieldsValues>({
    resolver: useYupValidationResolver(schema),
  });

  const onSubmit = (formValues: FieldsValues) => {
    setState({
      submit: true,
      success: false,
      error: false,
    });
    const { name, email } = formValues;

    const data = {
      name,
      jobLink: window.location.href,
      jobTitle,
      emailAddressList: [email],
      organizationName: 'MROADS',
    };

    API.getInstance().referAJob(data)
      .then(async () => {
        if (proceed) {
          proceed();
        }
        await confirmWrapper('', {
          variant: '',
          hideSecondaryBtn: true,
          showCloseButton: true,
          autoClose: true,
          disableBackdropClickClose: true,
          dialogContent: (
            <div className="flex flex-col items-center px-8 w-full max-h-[80vh] overflow-auto">
              <div>
                <BsFillPatchCheckFill size={70} color="#51a651" />
              </div>
              <div className="pt-8">
                <p
                  className="text-[#4a4a4a] text-2xl"
                  style={{ textAlign: 'center', marginTop: '0px' }}
                >
                  Shared Successfully.
                </p>
              </div>
            </div>
          ),
        });
      })
      .catch(async () => {
        await confirmWrapper('Something went wrong', {
          hideSecondaryBtn: true,
          variant: 'danger',
        });
      })
      .finally(() => {
        setState({
          ...defaultState,
        });
      });
  };

  return (
    <div className="flex justify-center items-center w-full h-full">
      <div className={twMerge(' rounded-lg w-[95%] sm:w-[90%] md:w-[70%] max-w-2xl')}>
        <div className="flex flex-col justify-center px-8 pt-20 pb-10 w-full max-h-[80vh] overflow-auto">
          <div className="text-center">
            <p className="text-[#4a4a4a] text-lg">Share this Job</p>
          </div>
          <div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Input
                label="Name"
                isMandatory
                field="name"
                placeholder="Name"
                error={errors.name}
                register={register}
                disabled={state.submit}
              />
              <Input
                label="Send To"
                isMandatory
                field="email"
                placeholder="Email"
                error={errors.email}
                register={register}
                disabled={state.submit}
              />
              <div className="text-center">
                <button
                  type="submit"
                  className="border-sky-200 hover:border-sky-400 disabled:border-[#A9A9A9] bg-[#00A6CF]/[0.8] hover:bg-[#00A6CF] mt-8 mb-10 border rounded-md w-32 disabled:w-40 h-10 text-white hover:text-slate-200"
                  disabled={state.submit}
                >
                  {state.submit ? (
                    <div className="flex justify-center items-center gap-1">
                      Please Wait
                      <ReactLoading
                        type="balls"
                        color="#fff"
                        height={30}
                        width={30}
                      />
                    </div>
                  )
                    : 'Send'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ShareJob;
